import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import countries from "../components/country";
import { msmartAxios } from "../api/axios";
import { useToast } from "../context/ToastContext";
import { getErrorMessage } from "../utils/errorMessage";

function MiradsRegister() {
  const  teamId  = 1000;
  const nav = useNavigate();
  const { notifySuccess, notifyError, notifyLoading } = useToast();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");

  const [managerName, setManagerName] = useState("miraddsiomanager");
  const [position, setPosition] = useState("Member");

  const [countryCode, setCountryCode] = useState(null); // store option object
  const [phoneNumber, setPhoneNumber] = useState("");

  const [teamName, setTeamName] = useState("");
  const [managers, setManagers] = useState([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // --- Select options ---
  const countryOptions = useMemo(() => {
    return countries.map((country) => ({
      value: country.dialCode,
      label: (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src={country.flag} alt={country.name} style={{ width: 18 }} />
          <span>{country.isoCode}</span>
          <span className="text-muted" style={{ fontSize: 12 }}>
            +{country.dialCode}
          </span>
        </div>
      ),
      name: country.name,
      isoCode: country.isoCode,
      dialCode: country.dialCode,
    }));
  }, []);

  const managerOptions = useMemo(() => {
    return managers.map((m) => ({
      value: m.username,
      label: m.nameInTeam || m.username,
    }));
  }, [managers]);

  const positionOptions = useMemo(
    () => [
      { value: "Member", label: "Salesperson/Agent (Close Sales Only)" },
      { value: "Manager & Member", label: "Team Leader (Manage Team & Close Sales)" },
      { value: "Manager", label: "Manager (Manage Team Only)" },
    ],
    []
  );

  const filterCountry = (option, inputValue) => {
    const q = inputValue.toLowerCase();
    return (
      option.data.name?.toLowerCase().includes(q) ||
      option.data.isoCode?.toLowerCase().includes(q) ||
      String(option.data.dialCode || "").includes(q)
    );
  };

  useEffect(() => {
    let mounted = true;

    async function fetchTeam() {
      try {
        const res = await msmartAxios.get(`/api/msmart/get/team/${teamId}`);
        // close loading toast
        // react-hot-toast loading returns id
        // but your notifyLoading returns toast.loading(msg)
        // so store it:
        // (workaround below: we simply show success later)
        if (!mounted) return;

        if (res.status === 201) {
          setTeamName(res.data?.team?.teamName || "");
          setManagers(res.data?.manager || []);
          notifySuccess("Team loaded");
        }
      } catch (err) {
        if (!mounted) return;
        notifyError("Failed to load team info");
      }
    }

    if (teamId) fetchTeam();
    return () => (mounted = false);
  }, [teamId, notifyLoading, notifySuccess, notifyError]);

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    const filteredValue = value.replace(/[^a-zA-Z0-9_]/g, "");
    setUsername(filteredValue);
  };

  const fullPhone = countryCode?.value
    ? `${countryCode.value}${phoneNumber}`
    : phoneNumber;

  const handleSubmit = async (e) => {
    e.preventDefault();

    // basic front-end checks
    if (!countryCode) return notifyError("Please select a country code");
    if (!managerName) return notifyError("Please select a manager");
    if (!position) return notifyError("Please select a job scope");
    if (password !== confirmPassword) return notifyError("Password confirmation does not match");

    setIsSubmitting(true);

    try {
      const data = {
        username,
        password,
        confirmPassword,
        name,
        email,
        managerName,
        phoneNumber: fullPhone,
        position,
      };

      const res = await msmartAxios.post(`/api/msmart/register/user/${teamId}`, data);

      if (res.status === 200) {
        notifySuccess("Registered successfully. Please sign in.");
        setTimeout(() => nav("/login"), 1200);
        return;
      }

      // fallback (just in case)
      notifyError(res.data?.error || "Registration failed");
    } catch (err) {
      notifyError(getErrorMessage(err, "Registration failed"));
    } finally {
      setIsSubmitting(false);
    }
  };

  // --- react-select styling to look modern + align with bootstrap inputs ---
  const selectStyles = {
    control: (base) => ({
      ...base,
      minHeight: 50,
      borderRadius: 14,
      boxShadow: "none",
    }),
  
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999, // ✅ penting
    }),
  
    menu: (base) => ({
      ...base,
      zIndex: 9999,
      borderRadius: 14,
      overflow: "hidden",
    }),
  };
  

  return (
    <div className="">
      <div className="container py-6">
        {/* Header */}
          <div className="text-center mb-4">
            <h1 className="header-title">
              Register
            </h1>

            {teamName && (
              <div className="mt-3">
                <span className="badge rounded-pill text-bg-light border px-3 py-2"
                      style={{ fontSize: 12, letterSpacing: 0.5 }}>
                  {teamName.toUpperCase()}
                </span>
              </div>
            )}
          </div>

        <div className="row justify-content-center">
          {/* mobile col-12, desktop col-6 */}
          <div className="col-12 col-lg-8">
            <div className="card shadow-sm" style={{ borderRadius: 18 }}>
              <div className="card-body p-4 p-md-5">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <div className="fw-bold" style={{ fontSize: 18 }}>
                      Create your account
                    </div>
                    <div className="text-muted" style={{ fontSize: 13 }}>
                      Fill in your details to start using the platform.
                    </div>
                  </div>
                </div>

                <hr className="my-4" />

                <form onSubmit={handleSubmit}>
                  {/* Username */}
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control shadow-none"
                      id="regUsername"
                      placeholder="Username"
                      value={username}
                      onChange={handleUsernameChange}
                      required
                    />
                    <label htmlFor="regUsername">Username (letters, numbers, underscore)</label>
                  </div>

                  {/* Name */}
                  <div className="form-floating mb-3">
                    <input
                      type="text"
                      className="form-control shadow-none"
                      id="regName"
                      placeholder="Name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                    />
                    <label htmlFor="regName">Full Name</label>
                  </div>

                  {/* Email */}
                  <div className="form-floating mb-3">
                    <input
                      type="email"
                      className="form-control shadow-none"
                      id="regEmail"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                    <label htmlFor="regEmail">Email</label>
                  </div>

                  {/* Phone */}
                  <div className="mb-3">
                    <div className="text-muted mb-1" style={{ fontSize: 13 }}>
                      Phone Number: <span className="fw-semibold">{countryCode ? `+${fullPhone}` : "—"}</span>
                    </div>

                    <div className="row g-2">
                      <div className="col-12 col-md-4">
                      <Select
                          styles={selectStyles}
                          options={countryOptions}
                          filterOption={filterCountry}
                          value={countryCode}
                          onChange={(opt) => setCountryCode(opt)}
                          placeholder="Country"

                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                        />
                      </div>

                      <div className="col-12 col-md-8">
                        <div className="form-floating">
                          <input
                            type="number"
                            className="form-control shadow-none"
                            id="regPhone"
                            placeholder="Phone"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                            required
                          />
                          <label htmlFor="regPhone">Phone (without country code)</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Password */}
                  <div className="mb-3">
                    <div className="form-floating position-relative">
                      <input
                        type={showPass ? "text" : "password"}
                        className="form-control shadow-none"
                        id="regPassword"
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        style={{ paddingRight: "3rem" }}
                      />
                      <label htmlFor="regPassword">Password</label>

                      <button
                        type="button"
                        className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                        style={{ border: "none" }}
                        onClick={() => setShowPass((v) => !v)}
                        tabIndex={-1}
                        title={showPass ? "Hide password" : "Show password"}
                      >
                        <i className={`bi ${showPass ? "bi-eye-slash" : "bi-eye"}`} style={{ fontSize: "1.1rem" }} />
                      </button>
                    </div>
                    <div className="text-muted mt-1" style={{ fontSize: 12 }}>
                      Minimum 6 characters recommended.
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div className="mb-4">
                    <div className="form-floating position-relative">
                      <input
                        type={showConfirm ? "text" : "password"}
                        className="form-control shadow-none"
                        id="regConfirmPassword"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        style={{ paddingRight: "3rem" }}
                      />
                      <label htmlFor="regConfirmPassword">Confirm Password</label>

                      <button
                        type="button"
                        className="btn btn-sm position-absolute top-50 end-0 translate-middle-y me-2 text-muted"
                        style={{ border: "none" }}
                        onClick={() => setShowConfirm((v) => !v)}
                        tabIndex={-1}
                        title={showConfirm ? "Hide password" : "Show password"}
                      >
                        <i className={`bi ${showConfirm ? "bi-eye-slash" : "bi-eye"}`} style={{ fontSize: "1.1rem" }} />
                      </button>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <div className="d-grid">
                    <button
                      type="submit"
                      className="btn btn-dark py-2"
                      disabled={isSubmitting}
                      style={{ borderRadius: 14 }}
                    >
                      {isSubmitting ? (
                        <span className="d-inline-flex align-items-center gap-2">
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          Registering...
                        </span>
                      ) : (
                        "Register"
                      )}
                    </button>
                  </div>
                  <div className="text-center text-muted mt-3" style={{ fontSize: 12 }}>
              By registering, you agree to the platform’s usage policy.
            </div>

                  <div className="text-center text-muted mt-3" style={{ fontSize: 13 }}>
                    Already have an account?{" "}
                    <Link to="/login" className="text-decoration-none">
                      Sign In
                    </Link>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MiradsRegister;
